import React from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams, createSearchParams } from "react-router-dom";
import { Header } from "../../../components/header";
import { Loading } from "../../../components/loading";
import { EmptyList } from "../../../components/empty-list";
import { Select, SearchBar } from "../../../components";
import { Table, TableHeader, TableBody, TableRow, TableCell } from "../../../components/tables";
import { SendIcon } from "../../../components/icons";
import { useProspections } from "../hooks";
import { useUsers } from "../../users/hooks";
import { CustomerSelectSearch } from "../../customers/components";
import { cleanObject } from "../../../helpers/clean-object";
import { ProspectionType } from "../../../__generated__/globalTypes";
import { prospectionTypeLabel } from "../../../helpers/enum-labels";
import { formatDisplayDate } from "../../../helpers/date";

interface ISearchForm {
  customerId?: string;
  sentById?: string;
  type?: string;
}

export const Prospections: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { data: usersData } = useUsers({ where: {}, limit: 100, offset: 0 });

  const form = useForm<ISearchForm>({
    mode: "all",
    defaultValues: {
      customerId: searchParams.get("customerId") ?? "",
      sentById: searchParams.get("sentById") ?? "",
      type: searchParams.get("type") ?? "",
    },
  });

  const onSubmit = () => {
    const values = form.getValues();
    navigate({
      pathname: "/prospections",
      search: `?${createSearchParams(cleanObject(values))}`,
    });
  };

  const customerId = searchParams.get("customerId");
  const sentById = searchParams.get("sentById");
  const type = searchParams.get("type");

  const where: any = {};
  if (customerId) where.customerId = +customerId;
  if (sentById) where.sentById = +sentById;
  if (type) where.type = type;

  const { data, loading } = useProspections({ where });

  return (
    <>
      <Header
        title="Prospections"
        subtitle="Historique des messages de démarchage"
        buttons={[
          {
            actionText: "Nouvelle prospection",
            bgColor: "indigo",
            textColor: "white",
            link: `/prospection/create`,
            icon: <SendIcon />,
          },
        ]}
      />

      <div className="main-container">
        <SearchBar
          form={form}
          onSubmit={onSubmit}
          placeholder=""
          advancedFilters={
            <>
              <CustomerSelectSearch form={form} name="customerId" />
              <Select label="Type" className="w-full" {...form.register("type")}>
                <option value="">Tous</option>
                {Object.keys(ProspectionType).map((key) => (
                  <option key={key} value={key}>
                    {prospectionTypeLabel(key)}
                  </option>
                ))}
              </Select>
              <Select label="Envoyé par" className="w-full" {...form.register("sentById")}>
                <option value="">Tous</option>
                {usersData?.users?.results?.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.firstname} {user.lastname}
                  </option>
                ))}
              </Select>
            </>
          }
        />

        <div className="p-4 mb-6 bg-white shadow rounded overflow-x-auto">
          {loading ? (
            <Loading />
          ) : !data?.prospections?.results?.length ? (
            <EmptyList text="Aucune prospection" />
          ) : (
            <Table>
              <TableHeader>
                <TableRow hover={false}>
                  <TableCell header>Type</TableCell>
                  <TableCell header>Contact</TableCell>
                  <TableCell header>Client</TableCell>
                  <TableCell header>Date</TableCell>
                  <TableCell header>Envoyé par</TableCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data?.prospections?.results?.map((prospection) => (
                  <TableRow
                    key={`prospection-${prospection.id}`}
                    onClick={() => navigate(`/prospection/${prospection.id}/update`)}
                  >
                    <TableCell>{prospectionTypeLabel(prospection.type)}</TableCell>
                    <TableCell>{prospection.email || prospection.phone || "-"}</TableCell>
                    <TableCell>
                      {prospection.customer?.name ?? "-"}
                      {prospection.customer?.site && (
                        <>
                          {" "}
                          <a
                            href={
                              prospection.customer.site.startsWith("http")
                                ? prospection.customer.site
                                : `https://${prospection.customer.site}`
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="text-indigo-600 hover:underline"
                          >
                            (site)
                          </a>
                        </>
                      )}
                    </TableCell>
                    <TableCell>{formatDisplayDate(prospection.createdAt)}</TableCell>
                    <TableCell>
                      {prospection.sentBy
                        ? `${prospection.sentBy.firstname} ${prospection.sentBy.lastname}`
                        : "-"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>
    </>
  );
};
